import type {
  XrefIntakes,
  XrefPumps,
  XrefSeals
} from "@/composables/forms/formTypes";

// "Nameplate HP Load Limit" "hp"
// "Operating RPM" "rpm"
// returns HP
export function shaft_adjusted_hp_load(
  nameplate_load: number | null | undefined,
  operating_rpm: number
): number {
  if (!nameplate_load) {
    return 0;
  }

  return nameplate_load * (operating_rpm / 3500);
}

export type ToleranceData = {
  pumps: {
    [index: string]: {
      operatingBurstPressurePsia: number;
      shaftPowerLimitStandardHP: number;
      shaftPowerLimitHighStrengthHP: number;
      operatingBurstRatioStandard: number;
      operatingBurstRatioHighStrength: number;
    };
  };
  seals: {
    [index: string]: {
      shaftPowerLimitStandardHP: number;
      shaftPowerLimitHighStrengthHP: number;
    };
  };
  intakes: {
    [index: string]: {
      shaftPowerLimitStandardHP: number;
      shaftPowerLimitHighStrengthHP: number;
    };
  };
};

export function getToleranceData(
  xrefPumps: XrefPumps = [],
  xrefSeals: XrefSeals = [],
  xrefIntakes: XrefIntakes = [],
  pumpIntakePressurePSIA: number,
  dischargePressurePSIA: number,
  operatingRPM: number
): ToleranceData {
  const result = {
    pumps: xrefPumps.reduce<ToleranceData["pumps"]>(
      (xrefPumpCalcs, xrefPump) => {
        xrefPumpCalcs[xrefPump.id] = {
          operatingBurstPressurePsia: 0,
          shaftPowerLimitStandardHP: 0,
          shaftPowerLimitHighStrengthHP: 0,
          operatingBurstRatioStandard: 0,
          operatingBurstRatioHighStrength: 0
        };
        return xrefPumpCalcs;
      },
      {}
    ),
    seals: xrefSeals.reduce<ToleranceData["seals"]>(
      (xrefSealCalcs, xrefSeal) => {
        xrefSealCalcs[xrefSeal.id] = {
          shaftPowerLimitStandardHP: 0,
          shaftPowerLimitHighStrengthHP: 0
        };
        return xrefSealCalcs;
      },
      {}
    ),
    intakes: xrefIntakes.reduce<ToleranceData["intakes"]>(
      (xrefIntakeCalcs, xrefIntake) => {
        xrefIntakeCalcs[xrefIntake.id] = {
          shaftPowerLimitStandardHP: 0,
          shaftPowerLimitHighStrengthHP: 0
        };
        return xrefIntakeCalcs;
      },
      {}
    )
  };

  xrefSeals.forEach(xrefSeal => {
    const seal = xrefSeal.r_seal;

    if (seal) {
      result.seals[xrefSeal.id].shaftPowerLimitStandardHP =
        shaft_adjusted_hp_load(seal.standard_shaft_limit_hp, operatingRPM);

      result.seals[xrefSeal.id].shaftPowerLimitHighStrengthHP =
        shaft_adjusted_hp_load(seal.high_strength_shaft_limit_hp, operatingRPM);
    }
  });

  xrefPumps.forEach(xrefPump => {
    const pump = xrefPump.r_pump;

    if (!pump) {
      return;
    }

    result.pumps[xrefPump.id].shaftPowerLimitStandardHP =
      shaft_adjusted_hp_load(pump.standard_shaft_rating_hp, operatingRPM);
    result.pumps[xrefPump.id].shaftPowerLimitHighStrengthHP =
      shaft_adjusted_hp_load(pump.high_strength_shaft_rating_hp, operatingRPM);

    const dhp = dischargePressurePSIA - pumpIntakePressurePSIA;

    result.pumps[xrefPump.id].operatingBurstPressurePsia = dhp;

    if (pump.standard_housing_pressure_rating_psi) {
      result.pumps[xrefPump.id].operatingBurstRatioStandard =
        dhp / pump.standard_housing_pressure_rating_psi;
    }

    if (pump.high_strength_housing_pressure_rating_psi) {
      result.pumps[xrefPump.id].operatingBurstRatioHighStrength =
        dhp / pump.high_strength_housing_pressure_rating_psi;
    }
  });

  xrefIntakes.forEach(xrefIntake => {
    const intake = xrefIntake.r_intake;
    if (!intake) {
      return;
    }

    result.intakes[xrefIntake.id].shaftPowerLimitStandardHP =
      shaft_adjusted_hp_load(intake.shaft_limit_hp, operatingRPM);

    result.intakes[xrefIntake.id].shaftPowerLimitHighStrengthHP =
      shaft_adjusted_hp_load(intake.high_strength_shaft_limit_hp, operatingRPM);
  });

  return result;
}
